import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, formatApiErrorDetail, API_BASE, TOKEN_KEY } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { HardDrive, Download, Trash2, Sparkles, Brain, ShieldOff, FileDown, TrendingUp, TrendingDown } from "lucide-react";

export default function AdminOps() {
  const [tab, setTab] = useState("backups");
  return (
    <AdminLayout subtitle="Modules 22 · 23 · 24 · 25" title="Ops · Backup · AI Insights · DSAR">
      <div className="flex flex-wrap gap-px bg-white/10 mb-6" data-testid="ops-tabs">
        {[["backups", "Backups"], ["insights", "AI Insights"], ["dsar", "DSAR"]].map(([v, l]) => (
          <button key={v} onClick={() => setTab(v)} className={`flex-1 min-w-[160px] px-4 py-3 text-sm bg-ec-ink transition-colors ${tab === v ? "border-b-2 border-ec-emerald text-white font-bold" : "text-zinc-400 hover:bg-white/5"}`} data-testid={`ops-tab-${v}`}>{l}</button>
        ))}
      </div>
      {tab === "backups" && <BackupsTab />}
      {tab === "insights" && <InsightsTab />}
      {tab === "dsar" && <DSARTab />}
    </AdminLayout>
  );
}

function BackupsTab() {
  const [rows, setRows] = useState([]);
  const [scope, setScope] = useState("full");
  const load = async () => { try { setRows((await api.get("/admin/backups")).data.rows); } catch (e) { console.error("load backups failed", e); } };
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { const { data } = await api.post("/admin/backups", { scope }); toast.success(`Backup created · ${data.total_documents} docs`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const download = async (id) => {
    const t = localStorage.getItem(TOKEN_KEY);
    const res = await fetch(`${API_BASE}/admin/backups/${id}/download`, { headers: { Authorization: `Bearer ${t}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `ethiccred_backup_${id}.json`; a.click(); URL.revokeObjectURL(url);
  };
  const del = async (id) => { if (!confirm("Delete this backup?")) return; await api.delete(`/admin/backups/${id}`); load(); };
  return (
    <Card className="rounded-sm border-white/10 bg-white/[0.02]" data-testid="backups-tab">
      <CardContent className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <HardDrive className="w-5 h-5 text-ec-emerald" />
          <h3 className="font-display text-xl font-bold text-white">Snapshots</h3>
          <div className="flex-1" />
          <select value={scope} onChange={(e) => setScope(e.target.value)} className="rounded-sm h-9 px-3 bg-white/5 border border-white/10 text-white text-sm" data-testid="backup-scope">
            {["full", "users", "certificates", "records"].map((s) => <option key={s} value={s} className="bg-ec-ink">{s}</option>)}
          </select>
          <Button onClick={create} className="rounded-sm bg-ec-emerald text-ec-ink gap-2" data-testid="backup-create"><HardDrive className="w-4 h-4" />Create backup</Button>
        </div>
        {rows.length === 0 ? <div className="py-8 text-center text-zinc-500" data-testid="backups-empty">No backups yet.</div> : (
          <ul className="divide-y divide-white/5">
            {rows.map((b) => (
              <li key={b.id} className="flex items-center gap-3 py-3" data-testid={`backup-${b.id}`}>
                <Badge className="bg-white/10 border-white/10 rounded-sm">{b.scope}</Badge>
                <span className="font-mono-plex text-sm text-white">{b.total_documents.toLocaleString()} docs</span>
                <span className="text-xs text-zinc-500">{b.size_kb} KB</span>
                <span className="text-xs font-mono-plex text-zinc-500 flex-1">{b.created_at?.slice(0, 16)}</span>
                <button onClick={() => download(b.id)} className="px-2 py-1 text-xs border border-white/10 rounded-sm text-white hover:bg-white/10 gap-1 flex items-center" data-testid={`backup-download-${b.id}`}><Download className="w-3 h-3" />Download</button>
                <button onClick={() => del(b.id)} className="p-1.5 border border-ec-error/30 text-ec-error rounded-sm hover:bg-ec-error/10" data-testid={`backup-delete-${b.id}`}><Trash2 className="w-3 h-3" /></button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function InsightsTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/insights").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="text-zinc-500 text-sm">Loading…</div>;
  const s = data.summary;
  return (
    <div className="space-y-6" data-testid="insights-tab">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10">
        <Kpi label="Users (30d)" value={s.users_30d} trend={s.users_growth_percent} testid="insight-users" />
        <Kpi label="Certs (30d)" value={s.certs_30d} trend={s.certs_growth_percent} testid="insight-certs" />
        <Kpi label="Revenue (30d)" value={`$${s.revenue_30d.toLocaleString()}`} testid="insight-revenue" />
        <Kpi label="Critical alerts" value={s.critical_alerts} dangerIf={s.critical_alerts > 0} testid="insight-alerts" />
      </div>
      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Brain className="w-5 h-5 text-ec-emerald" />
            <h3 className="font-display text-xl font-bold text-white">Investigation summaries</h3>
            <Badge className="bg-white/10 border-white/10 rounded-sm font-mono-plex text-[10px]">rule-based</Badge>
          </div>
          <ul className="space-y-2" data-testid="insights-list">
            {data.insights.map((i, idx) => (
              <li key={idx} className={`p-4 border-l-2 rounded-sm ${i.sentiment === "positive" ? "border-ec-emerald bg-ec-emerald/5" : i.sentiment === "negative" ? "border-ec-error bg-ec-error/5" : i.sentiment === "warning" ? "border-amber-500 bg-amber-500/5" : "border-white/10 bg-white/5"}`} data-testid={`insight-${idx}`}>
                <div className="text-sm text-white">{i.text}</div>
                <div className="eyebrow text-zinc-500 mt-1">{i.kind} · {i.sentiment}</div>
              </li>
            ))}
          </ul>
          <div className="mt-4 text-[11px] text-zinc-500 font-mono-plex">LLM enhancement: {data.llm_enhanced ? "ON" : "OFF"} · toggle from Trust & Safety → Settings.</div>
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ label, value, trend, dangerIf, testid }) {
  return (
    <div className={`p-5 bg-ec-ink ${dangerIf ? "border-l-2 border-ec-error" : ""}`} data-testid={testid}>
      <div className="eyebrow text-zinc-500">{label}</div>
      <div className="font-display text-3xl font-black mt-1 text-white">{value}</div>
      {typeof trend === "number" && (
        <div className={`text-[11px] mt-1 font-mono-plex flex items-center gap-1 ${trend >= 0 ? "text-ec-emerald" : "text-ec-error"}`}>
          {trend >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{Math.abs(trend)}% MoM
        </div>
      )}
    </div>
  );
}

function DSARTab() {
  const [rows, setRows] = useState([]);
  const [userId, setUserId] = useState("");
  const [reason, setReason] = useState("");
  const load = async () => { try { setRows((await api.get("/admin/dsar")).data.rows); } catch (e) { console.error("load dsar failed", e); } };
  useEffect(() => { load(); }, []);
  const run = async (action) => {
    if (!userId) { toast.error("Enter a user ID"); return; }
    if (action === "delete" && !confirm("Permanent deletion. Continue?")) return;
    try {
      const { data } = await api.post("/admin/dsar", { user_id: userId, action, reason });
      if (action === "export" && data.bundle) {
        const blob = new Blob([JSON.stringify(data.bundle, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob); const a = document.createElement("a"); a.href = url; a.download = `dsar_${userId}.json`; a.click(); URL.revokeObjectURL(url);
      }
      toast.success(action === "export" ? "Export downloaded" : "User deleted");
      setReason(""); setUserId("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-6" data-testid="dsar-tab">
      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <FileDown className="w-5 h-5 text-ec-emerald" />
            <h3 className="font-display text-xl font-bold text-white">Run a DSAR request</h3>
          </div>
          <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-3">
            <Input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="User ID" className="rounded-sm bg-white/5 border-white/10 text-white font-mono-plex" data-testid="dsar-user-id" />
            <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (recommended for audit log)" className="rounded-sm bg-white/5 border-white/10 text-white" data-testid="dsar-reason" />
            <div className="flex gap-2">
              <Button onClick={() => run("export")} className="rounded-sm bg-ec-emerald text-ec-ink gap-2" data-testid="dsar-export"><Download className="w-3 h-3" />Export</Button>
              <Button variant="outline" onClick={() => run("delete")} className="rounded-sm border-ec-error/40 text-ec-error hover:bg-ec-error/10 gap-2" data-testid="dsar-delete"><ShieldOff className="w-3 h-3" />Delete</Button>
            </div>
          </div>
          <p className="text-[11px] text-zinc-500 mt-3 font-mono-plex">Export ships a JSON bundle (user profile + records + certs + notifications + audit). Delete removes the user permanently and detaches their certs (recipient_redacted=true).</p>
        </CardContent>
      </Card>
      <Card className="rounded-sm border-white/10 bg-white/[0.02]">
        <CardContent className="p-6">
          <h3 className="font-display text-xl font-bold text-white mb-3">DSAR history</h3>
          {rows.length === 0 ? <div className="py-6 text-center text-zinc-500" data-testid="dsar-empty">No DSAR requests yet.</div> : (
            <ul className="divide-y divide-white/5 text-sm">
              {rows.map((r) => (
                <li key={r.id} className="py-2 flex items-center gap-3" data-testid={`dsar-${r.id}`}>
                  <Badge className="bg-white/10 border-white/10 rounded-sm w-20 justify-center">{r.action}</Badge>
                  <span className="font-mono-plex text-zinc-300 flex-1 truncate">{r.user_email || r.user_id}</span>
                  <span className="text-xs text-zinc-500">{r.created_at?.slice(0, 16)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
